import { computed, onMounted, onUnmounted, ref, toValue, watch } from 'vue'
import type { UseSearchConfig } from '@onxmaps/search-web-vue'
import { bboxFromVisibleRegion, type BBox } from '@/lib/geographic'
import { camera, visibleRegion } from '@/services/map-service'
import {
  getMapManager,
  getIsMapManagerReady,
} from '@/services/map-provider/map-manager-state'
import type { MapManager } from '@onxmaps/map-x'

/**
 * Augments a base search config with spatial data from the map viewer.
 * Injects `bounds` from the visible map region and `autocompleteConfig.location`
 * from the camera position, updating only on `map-x-camera-moveend` events.
 *
 * Safe to use before MapManager is ready — defers map event binding until
 * `getIsMapManagerReady()` becomes true, then listens for `map-x-ready`.
 */
export const useSearchSpatialFilter = (
  baseConfig: UseSearchConfig
): UseSearchConfig => {
  const bounds = ref<BBox | undefined>(
    bboxFromVisibleRegion(visibleRegion()) ?? undefined
  )
  const cameraLocation = ref<[number, number] | null>(
    camera()?.location.toDegreesTuple<[number, number]>() ?? null
  )

  let cleanupHandler: (() => void) | null = null
  let stopMapReadyWatch: (() => void) | null = null

  const updateSpatialData = () => {
    bounds.value = bboxFromVisibleRegion(visibleRegion()) ?? undefined
    cameraLocation.value =
      camera()?.location.toDegreesTuple<[number, number]>() ?? null
  }

  const initMapListeners = (mapManager: MapManager) => {
    mapManager.events.once('map-x-ready', () => {
      updateSpatialData()

      mapManager.events.on('map-x-camera-moveend', updateSpatialData)

      cleanupHandler = () => {
        mapManager.events.off('map-x-camera-moveend', updateSpatialData)
      }
    })
  }

  onMounted(() => {
    const mapManager = getMapManager()

    if (mapManager) {
      initMapListeners(mapManager)
      return
    }

    // MapManager not yet available — watch for it to become ready
    stopMapReadyWatch = watch(getIsMapManagerReady, (ready) => {
      if (ready) {
        const manager = getMapManager()
        if (manager) {
          initMapListeners(manager)
        }
        stopMapReadyWatch = null
      }
    })
  })

  onUnmounted(() => {
    stopMapReadyWatch?.()
    cleanupHandler?.()
  })

  const autocompleteConfig = computed(() => {
    const base = toValue(baseConfig.autocompleteConfig) ?? {}
    if (!cameraLocation.value) {
      return base
    }
    return {
      ...base,
      location: cameraLocation.value,
    }
  })

  return {
    ...baseConfig,
    bounds,
    autocompleteConfig,
  }
}
